import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import type { TMonthlyLogin, TMonthlyUser } from "../Tenants"

export type TReportTenant = { id: number; uid: string; name: string }

/**
 * A tenant user's month: as in the tenant's billing details, plus the
 * tenant and the days the account existed in the month.
 */
export type TMonthlyUserRow = TMonthlyUser & {
	tenant: TReportTenant
	created_at: string
	/** from signing up; null when not signed up by the month end */
	days_in_month: number | null
}

export type TMonthlyUserStatus =
	| "added"
	| "deleted"
	| "short_lived"
	| "no_logins"

export type TMonthlyUsersResponse = IListResponse<TMonthlyUserRow> & {
	meta: IListResponse<TMonthlyUserRow>["meta"] & {
		/** every login of the page's users in the month */
		logins: Array<TMonthlyLogin>
	}
}

export type TMonthlyTenantRow = {
	tenant: TReportTenant
	users_at_start: number
	new_users: number
	new_users_before_10th: number
	new_users_10th_to_19th: number
	new_users_from_20th: number
	deletions: number
	deletions_before_10th: number
	deletions_10th_to_19th: number
	deletions_from_20th: number
	users_at_end: number
	/** present for 15 days or fewer of the month */
	short_lived_users: number
	foreign_logins: number
	foreign_login_users: number
}

export type TChurnRisk = "high" | "medium" | "low" | "new" | "inactive"

export type TChurnRiskRow = {
	tenant: TReportTenant
	risk: TChurnRisk
	users: number
	recent_weekly_active_users: number
	baseline_weekly_active_users: number
	trend: number | null
	seat_use: number | null
	last_login_at: string | null
	days_since_last_login: number | null
	weekly_active_users: Array<number>
	in_trial: boolean
	on_hold: boolean
}

/**
 * Tenant filters shared by the reports, as on the tenants list.
 */
export type TTenantFiltersRequest = {
	signup_after?: string | null
	signup_before?: string | null
	supported_by_users?: Array<number>
}

export function tenantReportsXHR(xhr: XHRInstance) {
	return {
		async getMonthly(
			params: TTenantFiltersRequest & {
				month: string
				/** only tenants with logins from outside India in the month */
				outside_india?: 1
				q?: string
				page?: number
			}
		) {
			return xhr
				.get<
					IListResponse<TMonthlyTenantRow>
				>("/tenant-reports/monthly", { params })
				.then((resp) => resp.data)
		},
		async getMonthlyUsers(
			params: TTenantFiltersRequest & {
				month: string
				tenants?: Array<number>
				user_created_after?: string
				user_created_before?: string
				/** only users with logins from outside India in the month */
				outside_india?: 1
				status?: TMonthlyUserStatus
				q?: string
				page?: number
			}
		) {
			return xhr
				.get<TMonthlyUsersResponse>("/tenant-reports/monthly-users", {
					params,
				})
				.then((resp) => resp.data)
		},
		async getChurnRisk(
			params: TTenantFiltersRequest & {
				risks: Array<TChurnRisk>
				q?: string
				page?: number
			}
		) {
			return xhr
				.get<
					IListResponse<TChurnRiskRow>
				>("/tenant-reports/churn-risk", { params })
				.then((resp) => resp.data)
		},
	}
}
